// "Open in BaseMap" map deep links (WEB-7109), built by the MCP server's tools (API-3669) and opened
// by web, iOS (IOS-25822) and Android (ANDROID-19015). This is the format all three agree on:
//
//   https://app.basemap.com/map?bbox=-106.2,39.4,-105.8,39.7&layers=81,100&marker=123
//
// app.basemap.com because it is the host both apps claim (applinks:app.basemap.com on iOS, the
// app.basemap.com intent filters on Android), and it serves this web app too, so the same link opens
// the app where it's installed and the web map everywhere else. Each app also has to claim the /map
// path: the "paths" list in the apple-app-site-association file, and an intent filter on Android.
//
//   bbox    west,south,east,north in decimal degrees (WGS84). Fits the map to it.
//   point   longitude,latitude - longitude first, like bbox and GeoJSON. Centres the map on it.
//   zoom    0-20, with point. Without it a point opens at DEFAULT_POINT_ZOOM.
//   layers  layer ids, comma separated. Turned on as if the user had tapped each tile in the layer
//           menu: one basemap at a time, overlay companions. A locked one stays off and the app
//           offers the upgrade prompt.
//   marker  a saved marker's id. The map flies to it and opens it.
//   shape   a saved shape's id (line, polygon, circle or track - one id space, the measurement id).
//   unit    a hunting unit's valid_geo id. The link carries the unit's view itself: whoever builds it
//           also sends the unit's bbox and puts the unit layer in layers, both from get_unit_info.
//
// bbox wins over point, and an object's own bounds win over both. Anything the app can't read is
// ignored rather than failing the whole link.

const MAX_ZOOM = 20
export const DEFAULT_POINT_ZOOM = 14

export const MAP_DEEP_LINK_PARAMS = ['bbox', 'point', 'zoom', 'layers', 'marker', 'shape', 'unit']

const numbers = value => {
  if (typeof value !== 'string' || !value.trim()) return null
  const parts = value.split(',').map(p => p.trim())
  if (parts.some(p => p === '')) return null
  const out = parts.map(Number)
  return out.every(Number.isFinite) ? out : null
}

const validLng = v => v >= -180 && v <= 180
const validLat = v => v >= -85 && v <= 85

const id = value => (typeof value === 'string' && value.trim() ? value.trim() : null)

// The link in a location.search string, or null when it has none of the map params.
// Returns { view: { bbox } | { center, zoom } | null, layerIds: [number], marker, shape, unit }.
export const parseMapDeepLink = search => {
  const params = new URLSearchParams(search || '')
  if (!MAP_DEEP_LINK_PARAMS.some(p => params.has(p))) return null

  let view = null
  const bbox = numbers(params.get('bbox'))
  if (bbox && bbox.length === 4) {
    const [w, s, e, n] = bbox
    if (validLng(w) && validLng(e) && validLat(s) && validLat(n) && w < e && s < n) view = { bbox }
  }
  const point = numbers(params.get('point'))
  if (!view && point && point.length === 2 && validLng(point[0]) && validLat(point[1])) {
    const zoom = Number(params.get('zoom'))
    view = {
      center: point,
      zoom: params.has('zoom') && Number.isFinite(zoom) ? Math.max(0, Math.min(MAX_ZOOM, zoom)) : DEFAULT_POINT_ZOOM
    }
  }

  const layerIds = (params.get('layers') || '')
    .split(',')
    .map(p => p.trim())
    .filter(p => /^\d+$/.test(p))
    .map(Number)
    .filter((v, i, all) => all.indexOf(v) === i)

  return { view, layerIds, marker: id(params.get('marker')), shape: id(params.get('shape')), unit: id(params.get('unit')) }
}

// The search string with the map params taken out, so a reload or a shared address bar doesn't
// open the link again. Other params (the share-location ones, say) are left alone.
export const withoutMapDeepLink = search => {
  const params = new URLSearchParams(search || '')
  MAP_DEEP_LINK_PARAMS.forEach(p => params.delete(p))
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}

const METERS_PER_DEGREE_LAT = 111320

// [west, south, east, north] of a saved shape's geoJson string, or null when it has none.
// A circle is a Point with properties.radius in metres.
export const shapeBounds = geoJson => {
  let feature
  try {
    feature = (typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson).features[0]
  } catch (e) {
    return null
  }
  const geometry = feature && feature.geometry
  if (!geometry || !Array.isArray(geometry.coordinates)) return null

  if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates
    const radius = Number(feature.properties && feature.properties.radius)
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
    if (!(radius > 0)) return [lng, lat, lng, lat]
    const dLat = radius / METERS_PER_DEGREE_LAT
    const dLng = radius / (METERS_PER_DEGREE_LAT * Math.max(Math.cos((lat * Math.PI) / 180), 0.01))
    return [lng - dLng, lat - dLat, lng + dLng, lat + dLat]
  }

  const points = []
  const collect = c => {
    if (Array.isArray(c) && typeof c[0] === 'number') points.push(c)
    else if (Array.isArray(c)) c.forEach(collect)
  }
  collect(geometry.coordinates)
  const valid = points.filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
  if (valid.length === 0) return null
  const lngs = valid.map(p => p[0]), lats = valid.map(p => p[1])
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)]
}

// A point on the shape to open its info window at: the middle vertex of a line or track, the
// centre of a circle, and the first vertex of a polygon's outer ring (on its edge, so a click there
// lands on the shape).
export const shapeAnchor = geoJson => {
  let geometry
  try {
    geometry = (typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson).features[0].geometry
  } catch (e) {
    return null
  }
  const c = geometry && geometry.coordinates
  if (!Array.isArray(c)) return null
  if (geometry.type === 'Point') return c
  if (geometry.type === 'LineString' && c.length) return c[Math.floor(c.length / 2)]
  if (geometry.type === 'Polygon' && c[0] && c[0].length) return c[0][0]
  return null
}
